import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, GraduationCap, Briefcase, CheckCircle, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const STEPS = [
  { id: 'personal', label: 'Personal Info', icon: User },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'experience', label: 'Experience & Skills', icon: Briefcase },
  { id: 'done', label: 'All Done', icon: CheckCircle },
];

const OnboardingWizard = ({ onComplete }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    first_name: '', last_name: '', phone: '', city: '', state: '',
    current_designation: '', teaching_experience_years: '',
    education: [],
    skills: [],
    preferred_subjects: [], preferred_locations: [],
  });
  const [newSkill, setNewSkill] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  useEffect(() => {
    axios.get(`${API}/educator/profile`, { headers, withCredentials: true })
      .then(res => {
        const p = res.data;
        setProfile(prev => ({
          ...prev,
          first_name: p.first_name || '',
          last_name: p.last_name || '',
          phone: p.phone || '',
          city: p.city || '',
          state: p.state || '',
          current_designation: p.current_designation || '',
          teaching_experience_years: p.teaching_experience_years || '',
          education: p.education || [],
          skills: p.skills || [],
          preferred_subjects: p.preferred_subjects || [],
          preferred_locations: p.preferred_locations || [],
        }));
      })
      .catch(() => {});
  }, []);

  const updateField = (key, val) => setProfile(p => ({ ...p, [key]: val }));

  const addSkill = () => {
    const s = newSkill.trim();
    if (s && !profile.skills.includes(s)) {
      updateField('skills', [...profile.skills, s]);
      setNewSkill('');
    }
  };

  const removeSkill = (s) => updateField('skills', profile.skills.filter(x => x !== s));

  const addEducation = () => {
    updateField('education', [...profile.education, { degree: '', institution: '', year: '' }]);
  };

  const updateEdu = (i, key, val) => {
    const updated = [...profile.education];
    updated[i] = { ...updated[i], [key]: val };
    updateField('education', updated);
  };

  const removeEdu = (i) => updateField('education', profile.education.filter((_, idx) => idx !== i));

  const saveProgress = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/educator/profile`, profile, { headers, withCredentials: true });
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const nextStep = async () => {
    await saveProgress();
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const finishOnboarding = async () => {
    await saveProgress();
    await axios.put(`${API}/educator/profile`, { ...profile, onboarding_completed: true }, { headers, withCredentials: true }).catch(() => {});
    toast.success('Profile setup complete!');
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-5">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Complete Your Profile</h2>
          <p className="text-sm text-slate-400 mt-1">Help us match you with the best opportunities</p>
          {/* Step Indicators */}
          <div className="flex items-center gap-1 mt-4">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                  i < step ? 'bg-green-500 text-white' : i === step ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400'
                }`}>
                  {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`w-8 h-0.5 mx-1 ${i < step ? 'bg-green-500' : 'bg-slate-700'}`} />}
              </div>
            ))}
            <span className="text-xs text-slate-400 ml-2">{STEPS[step].label}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-personal">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input value={profile.first_name} onChange={e => updateField('first_name', e.target.value)} placeholder="First name" className="mt-1" data-testid="onboard-first-name" />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input value={profile.last_name} onChange={e => updateField('last_name', e.target.value)} placeholder="Last name" className="mt-1" data-testid="onboard-last-name" />
                </div>
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input value={profile.phone} onChange={e => updateField('phone', e.target.value)} placeholder="+91 9876543210" className="mt-1" data-testid="onboard-phone" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input value={profile.city} onChange={e => updateField('city', e.target.value)} placeholder="e.g. Mumbai" className="mt-1" data-testid="onboard-city" />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={profile.state} onChange={e => updateField('state', e.target.value)} placeholder="e.g. Maharashtra" className="mt-1" data-testid="onboard-state" />
                </div>
              </div>
              <div>
                <Label>Current Role / Designation</Label>
                <Input value={profile.current_designation} onChange={e => updateField('current_designation', e.target.value)} placeholder="e.g. PGT Mathematics" className="mt-1" data-testid="onboard-designation" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-education">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Add your educational qualifications</p>
                <Button variant="outline" size="sm" onClick={addEducation} data-testid="add-education-btn">+ Add</Button>
              </div>
              {profile.education.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <GraduationCap className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No education added yet. Click "Add" to start.</p>
                </div>
              )}
              {profile.education.map((edu, i) => (
                <div key={i} className="p-4 border rounded-lg space-y-3 relative">
                  <button onClick={() => removeEdu(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500 text-xs">Remove</button>
                  <div>
                    <Label>Degree / Qualification</Label>
                    <Input value={edu.degree} onChange={e => updateEdu(i, 'degree', e.target.value)} placeholder="e.g. B.Ed, M.A. English" className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Institution</Label>
                      <Input value={edu.institution} onChange={e => updateEdu(i, 'institution', e.target.value)} placeholder="University / College" className="mt-1" />
                    </div>
                    <div>
                      <Label>Year</Label>
                      <Input value={edu.year} onChange={e => updateEdu(i, 'year', e.target.value)} placeholder="2022" className="mt-1" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-skills">
              <div>
                <Label>Years of Teaching Experience</Label>
                <Input type="number" value={profile.teaching_experience_years} onChange={e => updateField('teaching_experience_years', e.target.value)} placeholder="e.g. 5" className="mt-1 max-w-32" data-testid="onboard-experience" />
              </div>
              <div>
                <Label>Skills & Subjects</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={newSkill} onChange={e => setNewSkill(e.target.value)} placeholder="e.g. Mathematics, Physics, CBSE..." className="flex-1" data-testid="onboard-skill-input"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                  />
                  <Button variant="outline" onClick={addSkill} data-testid="add-skill-btn">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {profile.skills.map((s) => (
                    <Badge key={s} variant="secondary" className="gap-1 cursor-pointer hover:bg-red-100" onClick={() => removeSkill(s)}>
                      {s} <span className="text-red-500">×</span>
                    </Badge>
                  ))}
                  {profile.skills.length === 0 && <p className="text-xs text-slate-400">Add skills to help us match you with relevant jobs</p>}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-8" data-testid="onboarding-step-done">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Profile Setup Complete!</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Great job! Your profile is set up. You can always update it from your Profile page. Now let's find your next opportunity!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
          <div>
            {step > 0 && step < 3 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onboard-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {step === 0 && (
              <Button variant="ghost" onClick={onComplete} className="text-slate-400" data-testid="onboard-skip-btn">
                Skip for now
              </Button>
            )}
          </div>
          <div>
            {step < 3 ? (
              <Button onClick={nextStep} disabled={saving} data-testid="onboard-next-btn">
                {saving ? 'Saving...' : step === 2 ? 'Finish' : 'Next'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={finishOnboarding} data-testid="onboard-done-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
